import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaEnvelope, FaBug } from 'react-icons/fa';

const Footer = () => {
    const [scrollY, setScrollY] = useState(0);
    const [codeSnippet, setCodeSnippet] = useState('console.log("Hello World");');

    const [bugMotion] = useState({
        top: Array.from({ length: 6 }, () => `${Math.random() * 90}%`),
        left: Array.from({ length: 6 }, () => `${Math.random() * 90}%`),
        rotate: [0, 15, -10, 30, -20, 0]
    });

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const snippets = [
                'while(alive) { work(); work(); work(); }',
                'git commit -m "final final real final fixed version 1.0"',
                'setTimeout(() => { console.log("I will do it now..."); }, 9999999);',
                'if (error) { google(); stackOverflow(); copyPaste(); }',
                'user.happiness = Error404;',
                'try { understandLegacyCode(); } catch (e) { cry(); }',
                'if (issue) { restartEditor(); restartComputer(); reinstallEverything(); }',
                '/* You only fail when you stop trying. */',
                '// But it worked on my machine'
            ];
            setCodeSnippet(snippets[Math.floor(Math.random() * snippets.length)]);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const containerVariants = {
        initial: { opacity: 0, y: 50 },
        animate: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3, staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        initial: { y: 20, opacity: 0 },
        animate: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    const orbVariants = {
        animate: {
            x: [0, 10, -10, 0],
            y: [0, -10, 10, 0],
            transition: { duration: 10, ease: "easeInOut", repeat: Infinity }
        }
    };

    return (
        <motion.footer
            className="bg-gray-950 text-white mt-20 pt-6 pb-4 relative overflow-hidden border-t border-gray-800"
            initial="initial"
            animate="animate"
            variants={containerVariants}
        >
            <div className="absolute inset-0 z-20" style={{ pointerEvents: 'none' }}>
                <motion.div
                    className="absolute text-gray-600 z-50"
                    style={{ pointerEvents: 'none' }}
                    animate={{
                        top: bugMotion.top,
                        left: bugMotion.left,
                        rotate: bugMotion.rotate,
                        scale: 1,
                        opacity: 0.85
                    }}
                    transition={{
                        top: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" },
                        left: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" },
                        rotate: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }
                    }}
                >
                    <FaBug size={24} className="text-gray-600" />
                </motion.div>
            </div>

            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    className="absolute w-32 h-32 rounded-full bg-gray-800 opacity-10 blur-xl"
                    style={{ left: '10%', top: '20%', transform: `translateY(${scrollY * 0.05}px)` }}
                    variants={orbVariants}
                    animate="animate"
                />
                <motion.div
                    className="absolute w-40 h-40 rounded-full bg-gray-700 opacity-5 blur-xl"
                    style={{ right: '15%', top: '40%', transform: `translateY(${scrollY * -0.02}px)` }}
                    variants={orbVariants}
                    animate="animate"
                />
                <motion.div
                    className="absolute w-24 h-24 rounded-full bg-gray-600 opacity-10 blur-xl"
                    style={{ left: '30%', bottom: '30%', transform: `translateY(${scrollY * 0.03}px)` }}
                    variants={orbVariants}
                    animate="animate"
                />
            </div>

            <div className="max-w-6xl mx-auto px-8 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 md:items-stretch">
                    <motion.div variants={itemVariants} className="flex flex-col h-full">
                        <h4 className="font-plex text-lg font-semibold text-gray-300 mb-4">Console Output</h4>
                        <motion.div
                            className="bg-gray-900 p-4 rounded-lg font-mono text-sm text-gray-400 border border-gray-800 h-full flex items-center"
                            initial={{ opacity: 0.8 }}
                            animate={{ opacity: [0.8, 1, 0.8] }}
                            transition={{ repeat: Infinity, duration: 5 }}
                        >
                            <code>{codeSnippet}</code>
                        </motion.div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="flex flex-col h-full">
                        <h4 className="font-plex text-lg font-semibold text-gray-300 mb-4">Let's Work On Something Together</h4>
                        <div className="flex flex-col space-y-3 justify-between h-full p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                            <a href="https://github.com/cshilin" target="_blank" rel="noopener noreferrer" className="flex items-center group">
                                <FaGithub className="text-gray-500 mr-4 text-xl group-hover:text-white transition-colors duration-300" />
                                <span className="text-gray-400 group-hover:text-white transition-colors duration-300">github.com/cshilin</span>
                            </a>
                            <a href="https://linkedin.com/in/cshilin" target="_blank" rel="noopener noreferrer" className="flex items-center group">
                                <FaLinkedin className="text-gray-500 mr-4 text-xl group-hover:text-white transition-colors duration-300" />
                                <span className="text-gray-400 group-hover:text-white transition-colors duration-300">linkedin.com/in/cshilin</span>
                            </a>
                            <a href="mailto:jeremychongshilin@gmail.com" className="flex items-center group">
                                <FaEnvelope className="text-gray-500 mr-4 text-xl group-hover:text-white transition-colors duration-300" />
                                <span className="text-gray-400 group-hover:text-white transition-colors duration-300">jeremychongshilin@gmail.com</span>
                            </a>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center"
                    variants={itemVariants}
                >
                    <motion.p
                        className="text-sm text-gray-500 mb-4 md:mb-0 flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        © {new Date().getFullYear()} Shi Lin Chong. All code has bugs. Even this.
                    </motion.p>

                    <motion.div
                        className="flex items-center space-x-3 font-mono text-sm text-gray-600 bg-gray-900 px-4 py-2 rounded-md border border-gray-800"
                    >
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                        >
                            ⦿
                        </motion.span>
                        <span>Version 1.0 — Last updated 07/05/2025</span>
                    </motion.div>
                </motion.div>
            </div>
        </motion.footer>
    );
};

export default Footer;
